/**
 * dependencyRefs.js - THE canonical dependency reference contract.
 *
 * This file exists in TWO byte-identical copies:
 *   lambda/layer/nodejs/dependencyRefs.js   (backend, via /opt/nodejs/dependencyRefs)
 *   frontend/src/utils/dependencyRefs.js    (frontend)
 * A jest test asserts the copies are identical - edit BOTH together.
 *
 * Canonical stored formats for a dependsOn entry (comma-separated string):
 *   "17"            - dependency on subtask ST#0017 in the SAME project/template
 *   "tmpl_<id>:17"  - cross-template ref to add-in template <id>, subtask 17
 *                     (legal in template records only)
 * Anything else is INVALID and must be surfaced loudly, never silently skipped.
 * Historical dialects (ULID prefixes, type suffixes) are known ONLY by the
 * migration script, never by runtime code.
 *
 * Link contract emitted by buildLinksFromSubtasks:
 *   { source: '<numeric ref string>', target: '<taskId>', type: 'FS',
 *     sourceProjectId: '<template id for cross-template refs, else null>' }
 * sourceProjectId is null for same-project deps so single-project consumers
 * (arrow renderer row guard) match without needing per-row project ids.
 */

const CANONICAL_NUMERIC = /^\d+$/;
// Underscore is part of the real id alphabet: every pool add-in id starts
// tmpl_POOL_ (generateId('tmpl_POOL_') in templateWrite). [A-Za-z0-9] alone
// rejected every pool add-in ref - Bill's ruling 2026-08-17, T153.
const CANONICAL_TEMPLATE = /^(tmpl_[A-Za-z0-9_]+):(\d+)$/;

/**
 * Parse a single canonical dependsOn entry.
 * Returns { numericRef, templateId, invalid: false } for canonical entries,
 * or { raw, invalid: true } for anything else.
 */
function parseDepRef(raw) {
    if (raw == null) return { raw, invalid: true };
    const str = String(raw).trim();
    if (!str) return { raw: str, invalid: true };

    if (CANONICAL_NUMERIC.test(str)) {
        return { numericRef: parseInt(str, 10), templateId: null, invalid: false };
    }

    const m = CANONICAL_TEMPLATE.exec(str);
    if (m) {
        return { numericRef: parseInt(m[2], 10), templateId: m[1], invalid: false };
    }

    return { raw: str, invalid: true };
}

/** ST#0017 from 17. */
function toSubtaskId(numericRef) {
    return `ST#${String(numericRef).padStart(4, '0')}`;
}

/**
 * Are two dependsOn entries the same reference?
 * Invalid entries never match anything (migration removes them; runtime
 * must not guess).
 */
function sameRef(a, b) {
    const pa = parseDepRef(a);
    const pb = parseDepRef(b);
    if (pa.invalid || pb.invalid) return false;
    return pa.numericRef === pb.numericRef && pa.templateId === pb.templateId;
}

/**
 * Build Gantt links for subtask dependencies - the ONLY link builder.
 * subtasks: array of task records with taskId, projectId?, dependsOn?,
 * dependencyColours?.
 * Returns { links, invalidDeps }:
 *   links      - link contract objects (colour attached when the stored
 *                dependencyColours map has a canonical-numeric key for the dep)
 *   invalidDeps - [{ taskId, projectId, raw }] for every non-canonical entry
 */
function buildLinksFromSubtasks(subtasks) {
    const links = [];
    const invalidDeps = [];

    for (const task of subtasks || []) {
        if (!task || !task.dependsOn || typeof task.dependsOn !== 'string') continue;
        const entries = task.dependsOn.split(',').map(d => d.trim()).filter(d => d);

        for (const entry of entries) {
            const parsed = parseDepRef(entry);
            if (parsed.invalid) {
                invalidDeps.push({ taskId: task.taskId, projectId: task.projectId, raw: parsed.raw });
                continue;
            }

            const source = String(parsed.numericRef);
            const link = {
                id: `link_${source}_${task.taskId}`,
                source,
                target: task.taskId,
                type: 'FS',
                sourceProjectId: parsed.templateId || null
            };

            // Colour keys are canonical numeric (post-migration)
            const colours = task.dependencyColours;
            if (colours && colours[source]) {
                link.color = colours[source];
            }

            links.push(link);
        }
    }

    return { links, invalidDeps };
}

module.exports = {
    parseDepRef,
    toSubtaskId,
    sameRef,
    buildLinksFromSubtasks
};
